import { useState } from "react";
import { Newspaper, AlertCircle, Plus, Download } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, AdminModal, EmptyState } from "@/components/admin/shared";
import { MarketingImageEditor } from "@/components/admin/images";
import {
  useNewsletterSubscribers,
  useNewsletterCampaigns,
  useCreateCampaign,
} from "@/hooks/admin/useMarketing";
import type {
  SubscriberStatus,
  CampaignStatus,
  CreateCampaignInput,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SUBSCRIBER_STATUS_LABELS: Record<SubscriberStatus, string> = {
  active: "Abonné",
  unsubscribed: "Désabonné",
};

const SUBSCRIBER_STATUS_STYLES: Record<SubscriberStatus, string> = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  unsubscribed: "bg-stone-100 text-stone-500 border-stone-200",
};

const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: "Brouillon",
  scheduled: "Planifiée",
  sent: "Envoyée",
};

const CAMPAIGN_STATUS_STYLES: Record<CampaignStatus, string> = {
  draft: "bg-stone-100 text-stone-600 border-stone-200",
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  sent: "bg-emerald-100 text-emerald-800 border-emerald-200",
};

// ---------------------------------------------------------------------------
// Tab nav
// ---------------------------------------------------------------------------

type Tab = "subscribers" | "campaigns";

function TabNav({ active, onChange }: { readonly active: Tab; readonly onChange: (t: Tab) => void }) {
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: "subscribers", label: "Abonnés" },
    { key: "campaigns", label: "Campagnes" },
  ];
  return (
    <div className="flex gap-1 border-b border-stone-200">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            active === tab.key
              ? "border-amber-500 text-amber-700"
              : "border-transparent text-stone-500 hover:text-stone-800"
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscribers tab
// ---------------------------------------------------------------------------

function SubscribersTab() {
  const [statusFilter, setStatusFilter] = useState<SubscriberStatus | "">("");
  const { data, isLoading, isError } = useNewsletterSubscribers({
    status: statusFilter || undefined,
  });

  const subscribers = data?.subscribers ?? [];

  const handleExport = () => {
    const csv = [
      "Email,Nom,Inscrit le,Statut",
      ...subscribers.map((s) =>
        [
          s.email,
          s.name ?? "",
          new Date(s.subscribed_at).toLocaleDateString("fr-SN"),
          SUBSCRIBER_STATUS_LABELS[s.status],
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "abonnes-newsletter.csv";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Export CSV téléchargé.");
  };

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-4">
        <KPICard label="Total abonnés" value={data?.total ?? 0} format="number" />
        <KPICard label="Actifs" value={data?.active ?? 0} format="number" />
        <KPICard label="Désabonnés" value={data?.unsubscribed ?? 0} format="number" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SubscriberStatus | "")}
          className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-300"
        >
          <option value="">Tous les statuts</option>
          {(Object.keys(SUBSCRIBER_STATUS_LABELS) as SubscriberStatus[]).map((s) => (
            <option key={s} value={s}>{SUBSCRIBER_STATUS_LABELS[s]}</option>
          ))}
        </select>
        <Button variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Exporter CSV
        </Button>
      </div>

      {/* Table */}
      {isLoading && (
        <div className="space-y-3 animate-pulse">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-12 rounded bg-stone-100" />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Impossible de charger les abonnés.
        </div>
      )}

      {!isLoading && !isError && subscribers.length === 0 && (
        <EmptyState
          icon={<Newspaper className="h-6 w-6" />}
          title="Aucun abonné"
          description="Les abonnés apparaîtront ici dès qu'ils s'inscrivent."
        />
      )}

      {!isLoading && !isError && subscribers.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Email</th>
                  <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Nom</th>
                  <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Inscrit le</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {subscribers.map((s) => (
                  <tr key={s.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-stone-900">{s.email}</td>
                    <td className="hidden md:table-cell px-4 py-3 text-stone-600">
                      {s.name ?? "—"}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-stone-500">
                      {new Date(s.subscribed_at).toLocaleDateString("fr-SN")}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${SUBSCRIBER_STATUS_STYLES[s.status]}`}
                      >
                        {SUBSCRIBER_STATUS_LABELS[s.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create campaign modal
// ---------------------------------------------------------------------------

function CreateCampaignModal({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const create = useCreateCampaign();
  const [subject, setSubject] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [imageUrl, setImageUrl] = useState("");

  const handleSubmit = () => {
    if (!subject.trim()) { toast.error("Le sujet est obligatoire."); return; }

    const input: CreateCampaignInput = {
      subject,
      scheduled_at: scheduledAt || null,
    };
    create.mutate(input, {
      onSuccess: () => { toast.success("Campagne créée."); onClose(); },
      onError: () => toast.error("Erreur lors de la création de la campagne."),
    });
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title="Nouvelle campagne"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "En cours..." : "Créer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Sujet <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="ex : Nouveautés Countre Dairy"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Date d'envoi planifié (optionnel)
          </label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Newsletter header image */}
        <MarketingImageEditor
          preset="email"
          value={imageUrl}
          onChange={(url) => setImageUrl(url)}
          label="Image d'en-tête (optionnel)"
        />
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Campaigns tab
// ---------------------------------------------------------------------------

function CampaignsTab() {
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, isError } = useNewsletterCampaigns();
  const campaigns = data?.campaigns ?? [];

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle campagne
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-3 animate-pulse">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-12 rounded bg-stone-100" />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          Impossible de charger les campagnes.
        </div>
      )}

      {!isLoading && !isError && campaigns.length === 0 && (
        <EmptyState
          icon={<Newspaper className="h-6 w-6" />}
          title="Aucune campagne"
          description="Créez votre première campagne newsletter."
          action={{ label: "Nouvelle campagne", onClick: () => setCreateOpen(true) }}
        />
      )}

      {!isLoading && !isError && campaigns.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Sujet</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Statut</th>
                  <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Destinataires</th>
                  <th className="hidden md:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Taux ouverture</th>
                  <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Planifiée le</th>
                  <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Envoyée le</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {campaigns.map((c) => (
                  <tr key={c.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-stone-900">{c.subject}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${CAMPAIGN_STATUS_STYLES[c.status]}`}
                      >
                        {CAMPAIGN_STATUS_LABELS[c.status]}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                      {c.recipients_count.toLocaleString("fr-SN")}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-right tabular-nums text-stone-600">
                      {c.status === "sent" ? `${c.open_rate.toFixed(1)} %` : "—"}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-stone-500">
                      {c.scheduled_at
                        ? new Date(c.scheduled_at).toLocaleDateString("fr-SN")
                        : "—"}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-stone-500">
                      {c.sent_at
                        ? new Date(c.sent_at).toLocaleDateString("fr-SN")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CreateCampaignModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Newsletter() {
  const [tab, setTab] = useState<Tab>("subscribers");

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Newspaper className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Newsletter
            </h1>
            <p className="text-sm text-stone-500">
              Abonnés et campagnes d'emailing
            </p>
          </div>
        </div>

        <TabNav active={tab} onChange={setTab} />

        {tab === "subscribers" && <SubscribersTab />}
        {tab === "campaigns" && <CampaignsTab />}
      </div>
    </AdminLayout>
  );
}
